import { Play, Spinner } from '@phosphor-icons/react';
import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { usePlayRecording } from 'src/contexts/PlayRecordingContext';
import { useNotification } from 'src/contexts/NotificationContext';

type CallRecordingButtonProps = {
  callId: string;
  recordingAvailable: boolean;
  showTimeline?: boolean;
  setCurrentTime?: (time: number) => void;
};

export interface CallRecordingButtonRef {
  click: () => void;
}

export const CallRecordingButton = forwardRef<
  CallRecordingButtonRef,
  CallRecordingButtonProps
>(
  (
    {
      callId,
      recordingAvailable,
      showTimeline = false,
      setCurrentTime = () => {},
    },
    ref,
  ) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const playTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { get } = useAuthedRequest();

    const notification = useNotification();

    const {
      audioUrl,
      setAudioUrl,
      audioRef,
      cleanupAudio: contextCleanupAudio,
      setPlayButtonClick,
    } = usePlayRecording();

    const handleLoadRecording = async (
      evt?: React.MouseEvent<HTMLButtonElement>,
    ) => {
      if (evt) {
        evt.stopPropagation();
      }
      if (!recordingAvailable) return;

      setLoading(true);
      try {
        const arrayBuffer = await get(`/calls/recording/${callId}`, {
          headers: {
            Accept: 'audio/mpeg',
          },
          responseType: 'arraybuffer',
        });
        const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setIsFinished(false);
        if (!showTimeline) {
          setIsModalOpen(true);
        }
      } catch (error) {
        console.error('Error fetching recording:', error);

        const errorMessages = {
          404: 'Recording not found',
          401: 'Unauthorized access to recording',
          403: 'Unauthorized access to recording',
        };

        const message =
          errorMessages[
            (error as { status: number }).status as keyof typeof errorMessages
          ] ?? 'Unexpected error trying to get the recording, please try again';

        notification.error(message);
      } finally {
        setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      click: () => handleLoadRecording(),
    }));

    useEffect(() => {
      setPlayButtonClick(() => handleLoadRecording);
    }, [setPlayButtonClick, callId, recordingAvailable]);

    const cleanupAudio = () => {
      contextCleanupAudio();
      setIsModalOpen(false);
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
        playTimeoutRef.current = null;
      }
    };

    const handlePlay = () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }
      setIsFinished(false);
    };

    const handlePause = () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }
    };

    const handleEnded = () => {
      setIsFinished(true);
      cleanupAudio();
    };

    const shouldShowPlayButton =
      !showTimeline ||
      (showTimeline && !audioUrl) ||
      (showTimeline && isFinished);

    return (
      <>
        {shouldShowPlayButton && (
          <Button
            disabled={loading}
            className="flex items-center drop-shadow-md"
            onClick={handleLoadRecording}
          >
            {loading ? <Spinner className="animate-spin" /> : <Play />}
          </Button>
        )}

        {showTimeline && audioUrl && (
          <div className="w-full mt-2">
            <audio
              ref={audioRef}
              controls
              src={audioUrl}
              onEnded={handleEnded}
              onPlay={handlePlay}
              onPause={handlePause}
              className="w-full"
              autoPlay
              onTimeUpdate={(e) => {
                setCurrentTime((e.target as HTMLAudioElement).currentTime);
              }}
            />
          </div>
        )}

        {!showTimeline && isModalOpen && (
          <Modal
            title="Call Recording"
            isOpen={isModalOpen}
            onClose={cleanupAudio}
            className="w-96"
          >
            <div className="text-center">
              <audio
                controls
                src={audioUrl || ''}
                onEnded={handleEnded}
                onPlay={handlePlay}
                onPause={handlePause}
                className="w-full bg-neutral-0"
                autoPlay
              />
            </div>
          </Modal>
        )}
      </>
    );
  },
);
